import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiError, API_BASE, apiFetch } from "@/lib/api";
import { Image as ImageIcon, Camera, UploadSimple, X, Trash, GridFour } from "@phosphor-icons/react";

const IMG_ACCEPT = ".png,.jpg,.jpeg,.webp,.gif";

// Upload an image File/Blob. Explicit uploads go into the reusable gallery; pass
// addToLibrary=false for auto-generated frames (they're stored but stay out of the gallery).
async function uploadImage(fileOrBlob, filename = "thumbnail.png", addToLibrary = true) {
  const fd = new FormData();
  fd.append("file", fileOrBlob, fileOrBlob.name || filename);
  fd.append("add_to_library", addToLibrary ? "true" : "false");
  const res = await apiFetch(`${API_BASE}/admin/images`, { method: "POST", body: fd });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.detail || "Image upload failed");
  }
  const d = await res.json();
  return d.url;
}

function isEmbedUrl(u) { return /youtube|youtu\.be|vimeo/i.test(u || "") || (u || "").startsWith("bunny://"); }

// Capture a decodable frame from a video as a JPEG blob. Resolves null on failure/timeout.
// The generous timeout matters: the first request for a freshly uploaded video makes the
// backend pull the whole file from object storage before serving byte one, which can take
// well over a minute for large videos.
function captureVideoFrame(url, seekTime = 1, timeoutMs = 90000) {
  return new Promise((resolve) => {
    let done = false;
    const v = document.createElement("video");
    const killer = setTimeout(() => finish(null), timeoutMs);
    function finish(val) {
      if (done) return;
      done = true;
      clearTimeout(killer);
      try { v.removeAttribute("src"); v.load(); } catch { /* noop */ }
      resolve(val);
    }
    const draw = () => {
      if (done) return;
      try {
        const canvas = document.createElement("canvas");
        canvas.width = v.videoWidth || 1280;
        canvas.height = v.videoHeight || 720;
        canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => finish(b || null), "image/jpeg", 0.82);
      } catch { finish(null); }
    };
    // Only draw once the browser actually has a decoded frame (readyState >= HAVE_CURRENT_DATA),
    // otherwise the canvas comes out black.
    const drawWhenReady = () => {
      if (done) return;
      if (v.readyState >= 2) { draw(); return; }
      v.addEventListener("canplay", draw, { once: true });
    };
    try {
      v.preload = "auto";
      v.muted = true;
      v.playsInline = true;
      v.crossOrigin = "anonymous";
      v.onloadedmetadata = () => {
        const dur = (v.duration && isFinite(v.duration)) ? v.duration : 0;
        const target = dur ? Math.min(seekTime, dur * 0.5) : 0;
        if (target > 0.05) {
          v.onseeked = () => drawWhenReady();
          try { v.currentTime = target; } catch { drawWhenReady(); }
          // Fallback: if seeking never completes (e.g. no range support), grab whatever frame loads.
          setTimeout(() => drawWhenReady(), 10000);
        } else {
          drawWhenReady();
        }
      };
      v.onerror = () => finish(null);
      v.src = url;
    } catch { finish(null); }
  });
}

// Auto-capture a frame from a same-origin video and upload it as a thumbnail. Returns url or null (best-effort).
export async function autoThumbnailFromVideo(url, seekTime = 1) {
  if (!url || isEmbedUrl(url)) return null;
  const blob = await captureVideoFrame(url, seekTime);
  if (!blob) return null;
  // Upload deliberately happens OUTSIDE the capture timeout so a slow upload can't discard a good frame.
  try { return await uploadImage(blob, "auto-frame.jpg", false); } catch { return null; }
}

// ---- Reusable gallery picker modal ----
export function ImageGalleryModal({ onPick, onClose }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const reload = () => api.get("/admin/images").then((r) => setItems(r.data || [])).catch((e) => setErr(formatApiError(e)));
  useEffect(() => { reload(); }, []);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Remove this image from the gallery? Lessons already using it keep working.")) return;
    setDeletingId(id); setErr("");
    try { await api.delete(`/admin/images/${id}`); await reload(); }
    catch (er) { setErr(formatApiError(er)); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose} data-testid="image-gallery-modal">
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#DCDEE2] rounded-2xl border border-[#C4C8CE] w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#C4C8CE]">
          <h3 className="font-bold font-display flex items-center gap-2"><GridFour size={18} color="#CCFF00" /> Choose from gallery</h3>
          <button className="btn-ghost p-1" onClick={onClose} data-testid="gallery-close"><X size={18} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {err && <div className="text-[#FF5100] text-sm mb-2">{err}</div>}
          {items === null && <div className="text-[#000000] text-sm">Loading images…</div>}
          {items && items.length === 0 && <div className="text-[#000000] text-sm italic">No images uploaded yet. Upload one and it will appear here for reuse.</div>}
          {items && items.length > 0 && (
            <>
              <p className="text-xs text-[#000000] mb-3">Click an image to use it. Hover to remove one from the gallery.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {items.map((it) => (
                  <div key={it.id} className="group relative" data-testid={`gallery-item-${it.id}`}>
                    <button
                      type="button" data-testid={`gallery-image-${it.id}`}
                      onClick={() => { onPick(it.url); onClose(); }}
                      className="w-full aspect-video rounded-lg overflow-hidden border border-[#C4C8CE] hover:border-[#CCFF00] hover:ring-2 hover:ring-[#CCFF00]/40 transition-all bg-[#F4F5F6] grid place-items-center cursor-pointer"
                    >
                      <img src={it.url} alt={it.filename || "thumbnail"} className="w-full h-full object-contain" loading="lazy" />
                    </button>
                    <button
                      type="button" data-testid={`gallery-delete-${it.id}`}
                      onClick={(e) => handleDelete(e, it.id)}
                      disabled={deletingId === it.id}
                      title="Remove from gallery"
                      className="absolute top-1 right-1 grid place-items-center w-6 h-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-[#FF5100] disabled:opacity-50"
                    >
                      <Trash size={13} weight="bold" />
                    </button>
                    {it.filename && (
                      <div className="mt-1 text-[10px] text-[#000000] truncate" title={it.filename}>{it.filename}</div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ---- Compact thumbnail picker (used for pillar default thumbnails) ----
export function CompactThumbnailPicker({ value, onChange, label = "Default thumbnail", testidPrefix = "pillar-thumb" }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showGallery, setShowGallery] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    setErr(""); setBusy(true);
    try { await onChange(await uploadImage(f)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`${testidPrefix}-picker`}>
      <span className="text-[10px] uppercase tracking-[0.1em] text-[#000000] hidden sm:inline">{label}</span>
      {value ? (
        <img src={value} alt={label} className="w-20 aspect-video object-cover rounded border border-[#C4C8CE]" data-testid={`${testidPrefix}-preview`} />
      ) : (
        <span className="w-20 aspect-video rounded border border-dashed border-[#C4C8CE] grid place-items-center text-[9px] text-[#000000]">none</span>
      )}
      <input ref={fileRef} type="file" accept={IMG_ACCEPT} className="hidden" data-testid={`${testidPrefix}-file`} onChange={(e) => handleFile(e.target.files?.[0])} />
      <button type="button" className="btn-ghost p-1.5 text-[#000000]" title="Upload image" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testidPrefix}-upload-btn`}>
        <UploadSimple size={15} />
      </button>
      <button type="button" className="btn-ghost p-1.5 text-[#000000]" title="Choose from gallery" onClick={() => setShowGallery(true)} data-testid={`${testidPrefix}-gallery-btn`}>
        <GridFour size={15} />
      </button>
      {value && (
        <button type="button" className="btn-ghost p-1.5 text-[#FF5100]" title="Remove" onClick={() => onChange("")} data-testid={`${testidPrefix}-remove-btn`}>
          <Trash size={15} />
        </button>
      )}
      {err && <span className="text-[#FF5100] text-[10px]">{err}</span>}
      {showGallery && <ImageGalleryModal onPick={(u) => onChange(u)} onClose={() => setShowGallery(false)} />}
    </div>
  );
}

// ---- Lesson-level thumbnail: upload / pick from gallery / paste URL ----
export function LessonThumbnail({ value, onChange, autoSuggested }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showGallery, setShowGallery] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (f) => {
    if (!f) return;
    setErr(""); setBusy(true);
    try { onChange(await uploadImage(f)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="card mb-5" data-testid="lesson-thumbnail-section">
      <h2 className="text-lg font-bold font-display flex items-center gap-1.5 mb-1"><ImageIcon size={18} color="#00B8D4" /> Lesson thumbnail</h2>
      <p className="text-xs text-[#000000] mb-3">Shown on library cards, the course matrix and the student curriculum. Recommended <strong>16:9 landscape · 1280×720</strong>. Upload a new image or reuse one from your gallery.</p>
      {err && <div className="text-[#FF5100] text-sm mb-2" data-testid="lesson-thumbnail-error">{err}</div>}
      {autoSuggested && value && (
        <div className="text-[11px] text-[#7a8a00] mb-2 flex items-center gap-1" data-testid="lesson-thumbnail-auto-hint">
          <ImageIcon size={12} weight="fill" /> Auto-selected from this pillar — change it below if you like.
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        {value ? (
          <img src={value} alt="lesson thumbnail" className="w-48 aspect-video object-cover rounded-lg border border-[#C4C8CE]" data-testid="lesson-thumbnail-preview" />
        ) : (
          <div className="w-48 aspect-video rounded-lg border border-dashed border-[#C4C8CE] grid place-items-center text-[11px] text-[#000000] text-center px-2">No thumbnail<br/>16:9 · 1280×720</div>
        )}
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept={IMG_ACCEPT} className="hidden" data-testid="lesson-thumbnail-file" onChange={(e) => handleFile(e.target.files?.[0])} />
          <div className="flex gap-2 flex-wrap">
            <button type="button" className="btn-secondary text-sm flex items-center gap-1" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="lesson-thumbnail-upload-btn">
              <UploadSimple size={14} weight="bold" /> {busy ? "Uploading…" : (value ? "Replace" : "Upload")}
            </button>
            <button type="button" className="btn-secondary text-sm flex items-center gap-1" onClick={() => setShowGallery(true)} data-testid="lesson-thumbnail-gallery-btn">
              <GridFour size={14} /> Gallery
            </button>
            {value && (
              <button type="button" className="btn-ghost text-sm text-[#FF5100] flex items-center gap-1" onClick={() => onChange("")} data-testid="lesson-thumbnail-remove-btn">
                <Trash size={14} /> Remove
              </button>
            )}
          </div>
          <input className="input-base text-xs w-64 max-w-full" placeholder="…or paste an image URL" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="lesson-thumbnail-url" />
        </div>
      </div>
      {showGallery && <ImageGalleryModal onPick={onChange} onClose={() => setShowGallery(false)} />}
    </div>
  );
}

// ---- Per-video thumbnail: capture frame from the video, upload, or pick from gallery ----
export function VideoThumbnail({ video, onChange }) {
  const [showCapture, setShowCapture] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const canCapture = video?.url && !isEmbedUrl(video.url);
  // Bunny thumbnails 404 while the video is still queued/transcoding. While that's
  // happening we poll Bunny's encoding status and show a live processing progress bar
  // (separate from the upload bar), retrying the image until it appears.
  const isBunnyThumb = (video?.thumbnail_url || "").startsWith("/api/bunny/thumb/");
  const bunnyGuid = isBunnyThumb ? video.thumbnail_url.split("/").pop().split("?")[0] : null;
  const [thumbTry, setThumbTry] = useState(0);
  const [thumbWaiting, setThumbWaiting] = useState(false);
  const [proc, setProc] = useState(null); // { label, progress, failed }
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPolling(), []);

  const startPolling = () => {
    if (!bunnyGuid || pollRef.current) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/bunny/video-status/${bunnyGuid}`);
        if (data.failed) { stopPolling(); setProc({ failed: true }); return; }
        setProc(data);
        // Retry the image on every tick — Bunny publishes the thumbnail mid-transcode.
        // Polling only stops once the image actually loads (onThumbLoaded).
        setThumbTry((n) => n + 1);
      } catch {
        setThumbTry((n) => n + 1);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 5000);
  };

  const onThumbLoaded = () => { stopPolling(); setThumbWaiting(false); setProc(null); };
  const onThumbError = () => {
    if (!isBunnyThumb) return;
    setThumbWaiting(true);
    startPolling();
  };

  const handleFile = async (f) => {
    if (!f) return;
    setErr(""); setBusy(true);
    try { onChange(await uploadImage(f)); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="mt-2 pl-7 flex items-center gap-2 flex-wrap" data-testid={`video-thumbnail-controls`}>
      <span className="text-[10px] uppercase tracking-[0.1em] text-[#000000]">Thumbnail</span>
      {video?.thumbnail_url ? (
        thumbWaiting ? (
          <span className="flex flex-col gap-1 w-40" data-testid="video-thumbnail-processing">
            {proc?.failed ? (
              <span className="text-[10px] text-[#FF5100] leading-tight" data-testid="video-processing-failed">
                Video processing failed on Bunny — try re-uploading this video.
              </span>
            ) : (
              <>
                <span className="flex items-center justify-between text-[10px] text-[#00768a] leading-tight">
                  <span data-testid="video-processing-label">{proc?.label || "Processing"}…</span>
                  <span data-testid="video-processing-percent">{proc?.progress ?? 0}%</span>
                </span>
                <span className="block h-1.5 rounded-full bg-[#C4C8CE] overflow-hidden">
                  <span className="block h-full bg-[#00B8D4] transition-all duration-500" style={{ width: `${Math.max(proc?.progress ?? 0, 3)}%` }} data-testid="video-processing-bar" />
                </span>
                <span className="text-[9px] text-[#000000] leading-tight">Upload done — Bunny is preparing the video &amp; thumbnail</span>
              </>
            )}
            <img src={`${video.thumbnail_url}${video.thumbnail_url.includes("?") ? "&" : "?"}r=${thumbTry}`} alt="" className="hidden" onLoad={onThumbLoaded} onError={onThumbError} key={thumbTry} />
          </span>
        ) : (
          <img src={isBunnyThumb ? `${video.thumbnail_url}?r=${thumbTry}` : video.thumbnail_url} alt="video thumbnail" className="w-20 aspect-video object-cover rounded border border-[#C4C8CE]" data-testid="video-thumbnail-preview" onError={onThumbError} />
        )
      ) : (
        <span className="w-20 aspect-video rounded border border-dashed border-[#C4C8CE] grid place-items-center text-[9px] text-[#000000]">none</span>
      )}
      {canCapture && (
        <button type="button" className="btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 text-[#00768a]" onClick={() => setShowCapture(true)} data-testid="video-capture-frame-btn">
          <Camera size={13} /> Capture frame
        </button>
      )}
      <input ref={fileRef} type="file" accept={IMG_ACCEPT} className="hidden" data-testid="video-thumbnail-file" onChange={(e) => handleFile(e.target.files?.[0])} />
      <button type="button" className="btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 text-[#000000]" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="video-thumbnail-upload-btn">
        <UploadSimple size={13} /> {busy ? "…" : "Upload"}
      </button>
      <button type="button" className="btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 text-[#000000]" onClick={() => setShowGallery(true)} data-testid="video-thumbnail-gallery-btn">
        <GridFour size={13} /> Gallery
      </button>
      {video?.thumbnail_url && (
        <button type="button" className="btn-ghost text-[11px] px-2 py-1 text-[#FF5100]" onClick={() => onChange("")} data-testid="video-thumbnail-remove-btn">Remove</button>
      )}
      {err && <span className="text-[#FF5100] text-[11px] w-full">{err}</span>}
      {showCapture && (
        <FrameCaptureModal
          url={video.url}
          onClose={() => setShowCapture(false)}
          onCaptured={(u) => { onChange(u); setShowCapture(false); }}
        />
      )}
      {showGallery && <ImageGalleryModal onPick={onChange} onClose={() => setShowGallery(false)} />}
    </div>
  );
}

// ---- Scrub-and-capture modal ----
function FrameCaptureModal({ url, onClose, onCaptured }) {
  const videoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const capture = async () => {
    const v = videoRef.current;
    if (!v) return;
    setErr(""); setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 1280;
      canvas.height = v.videoHeight || 720;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve, reject) =>
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not read frame — the video may be cross-origin"))), "image/jpeg", 0.85)
      );
      const url2 = await uploadImage(blob, "frame.jpg", false);
      onCaptured(url2);
    } catch (e) {
      setErr(e.message || "Capture failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} data-testid="frame-capture-modal">
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-[#DCDEE2] rounded-2xl border border-[#C4C8CE] w-full max-w-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[#C4C8CE]">
          <h3 className="font-bold font-display flex items-center gap-2"><Camera size={18} color="#CCFF00" /> Capture thumbnail from video</h3>
          <button className="btn-ghost p-1" onClick={onClose} data-testid="capture-close"><X size={18} /></button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <p className="text-xs text-[#000000]">Scrub the player to the exact moment you want, pause there, then click <strong>Capture this frame</strong>.</p>
          <div className="bg-black rounded-lg overflow-hidden">
            <video ref={videoRef} src={url} controls preload="metadata" playsInline className="w-full max-h-[50vh] object-contain bg-black" data-testid="capture-video" />
          </div>
          {err && <div className="text-[#FF5100] text-sm" data-testid="capture-error">{err}</div>}
          <button type="button" className="btn-primary self-start flex items-center gap-1.5" onClick={capture} disabled={busy} data-testid="capture-frame-confirm">
            <Camera size={16} weight="bold" /> {busy ? "Capturing…" : "Capture this frame"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
